import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import styled from 'styled-components';

// aria-activedescendant/id must be whitespace-free; option.value (e.g. a supplier name like
// "acme energy") isn't safe to use directly, so option ids are built from list position instead.

interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectFieldProps {
  id: string;
  name: string;
  value: string;
  options: SearchableSelectOption[];
  disabled?: boolean;
  placeholder: string;
  hasError?: boolean;
  /** Noun used to build a11y labels, e.g. 'tariff' -> "Clear tariff search", "No matching tariffs" */
  itemLabel: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}

const normalizeForSearch = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const isSubsequenceMatch = (query: string, candidate: string) => {
  let queryIndex = 0;

  for (let i = 0; i < candidate.length && queryIndex < query.length; i += 1) {
    if (candidate[i] === query[queryIndex]) {
      queryIndex += 1;
    }
  }

  return queryIndex === query.length;
};

const levenshteinDistance = (a: string, b: string) => {
  const rows = a.length + 1;
  const cols = b.length + 1;
  const matrix = Array.from({ length: rows }, () => Array<number>(cols).fill(0));

  for (let row = 0; row < rows; row += 1) {
    matrix[row][0] = row;
  }

  for (let col = 0; col < cols; col += 1) {
    matrix[0][col] = col;
  }

  for (let row = 1; row < rows; row += 1) {
    for (let col = 1; col < cols; col += 1) {
      const substitutionCost = a[row - 1] === b[col - 1] ? 0 : 1;
      matrix[row][col] = Math.min(
        matrix[row - 1][col] + 1,
        matrix[row][col - 1] + 1,
        matrix[row - 1][col - 1] + substitutionCost
      );
    }
  }

  return matrix[a.length][b.length];
};

const tokenFuzzyMatch = (queryToken: string, candidateToken: string) => {
  if (candidateToken.includes(queryToken)) {
    return true;
  }

  if (queryToken.length <= 2) {
    return false;
  }

  const maxDistance = queryToken.length <= 4 ? 1 : 2;
  return levenshteinDistance(queryToken, candidateToken) <= maxDistance;
};

const fuzzyScore = (query: string, candidate: string) => {
  const normalizedQuery = normalizeForSearch(query);
  const normalizedCandidate = normalizeForSearch(candidate);

  if (!normalizedQuery) {
    return 1;
  }

  if (normalizedCandidate.includes(normalizedQuery)) {
    return 100;
  }

  const queryTokens = normalizedQuery.split(' ').filter(Boolean);
  const candidateTokens = normalizedCandidate.split(' ').filter(Boolean);

  if (!queryTokens.length || !candidateTokens.length) {
    return 0;
  }

  const allTokensMatch = queryTokens.every((queryToken) =>
    candidateTokens.some((candidateToken) => tokenFuzzyMatch(queryToken, candidateToken))
  );

  if (allTokensMatch) {
    return 60;
  }

  if (
    normalizedQuery.length >= 3 &&
    isSubsequenceMatch(normalizedQuery.replace(/\s+/g, ''), normalizedCandidate.replace(/\s+/g, ''))
  ) {
    return 40;
  }

  return 0;
};

const Wrapper = styled.div`
  position: relative;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const StyledInput = styled.input<{ $hasClearButton: boolean }>`
  /* border, font-size, height and color are intentionally left to the shared .form-control
     class so this matches the sibling react-bootstrap Form.Control fields exactly (including
     disabled state), rather than duplicating (and risking drifting from) those rules here. */
  width: 100%;
  padding: 0.375rem ${(props) => (props.$hasClearButton ? '6.25rem' : '0.75rem')} 0.375rem 0.75rem;

  /* Disabled <select> fields render their placeholder-like option at full text opacity, but
     browsers dim ::placeholder text by default - match that so both fields look equally disabled. */
  &:disabled::placeholder {
    color: inherit;
    opacity: 1;
  }

  &.error {
    border-color: ${(props) => props.theme.forms.formControl['&.error'].borderColor};
  }

  &:focus {
    outline: 0;
    border-color: ${(props) => props.theme.colors.primaryHover};
    box-shadow: 0 0 0 0.2rem ${(props) => props.theme.colors.primaryHover}26;
  }
`;

const Controls = styled.div`
  position: absolute;
  top: 50%;
  right: calc(1em - 1px);
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 0;
  padding: 0;
  margin: 0;
  line-height: 1;
  color: ${(props) => props.theme.colors.light};

  &:hover {
    color: ${(props) => props.theme.colors.dark};
  }
`;

const Divider = styled.span`
  width: 1px;
  height: 20px;
  background: ${(props) => props.theme.colors.light};
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 0;
  padding: 0;
  width: 20px;
  height: 20px;
`;

const Arrow = styled.span<{ open: boolean }>`
  width: 12px;
  height: 8px;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 10 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='currentColor' stroke-width='1' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-size: 12px 8px;
  transform: ${(props) => (props.open ? 'rotate(180deg)' : 'rotate(0)')};
  transition: transform 0.15s ease;
`;

const OptionsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  list-style: none;
  margin: 0;
  margin-top: 0.25rem;
  padding: 0;
  border: ${(props) => props.theme.forms.formControl.border};
  border-radius: 0.25rem;
  max-height: 264px;
  overflow-y: auto;
  background: ${(props) => props.theme.colors.white};
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
`;

const OptionButton = styled.button<{ selected: boolean; highlighted: boolean }>`
  width: 100%;
  text-align: left;
  border: 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.light};
  background: ${(props) => (props.selected || props.highlighted ? props.theme.colors.light : props.theme.colors.white)};
  padding: 0.6rem 0.75rem;
  min-height: 44px;
  font-size: 14px;

  &:hover {
    background: ${(props) => props.theme.colors.light};
  }

  &:last-child {
    border-bottom: 0;
  }
`;

const EmptyState = styled.li`
  padding: 0.6rem 0.75rem;
  color: ${(props) => props.theme.colors.grey};
`;

const SearchableSelectField = ({
  id,
  name,
  value,
  options,
  disabled = false,
  placeholder,
  hasError = false,
  itemLabel,
  onChange,
  onBlur,
}: SearchableSelectFieldProps) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Mirrors isOpen for use inside the blur setTimeout below, so it can tell whether the
  // outside-mousedown handler already closed the dropdown (and thus already called onBlur).
  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const selectedOption = useMemo(() => options.find((option) => option.value === value), [options, value]);

  useEffect(() => {
    if (!isUserTyping) {
      setSearchValue(selectedOption?.label ?? '');
    }
  }, [isUserTyping, selectedOption]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onClickOutside = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setIsUserTyping(false);
        onBlur();
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [isOpen, onBlur]);

  const filteredOptions = useMemo(() => {
    if (!searchValue) return options;

    return options
      .map((option) => ({
        option,
        score: fuzzyScore(searchValue, option.label),
      }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score || a.option.label.localeCompare(b.option.label))
      .map(({ option }) => option);
  }, [searchValue, options]);

  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1);
      return;
    }

    if (!searchValue) {
      setHighlightedIndex(filteredOptions.findIndex((option) => option.value === value));
      return;
    }

    setHighlightedIndex(0);
  }, [isOpen, searchValue, value, filteredOptions]);

  const handleSelect = (option: SearchableSelectOption) => {
    setSearchValue(option.label);
    setIsUserTyping(false);
    setIsOpen(false);
    onChange(option.value);
  };

  const handleClear = () => {
    setSearchValue('');
    setIsUserTyping(false);
    setIsOpen(true);
    onChange('');
    inputRef.current?.focus();
  };

  const handleWidgetBlur = () => {
    if (disabled) return;

    // Defer so a click on the clear/toggle buttons (which steal focus) can be told apart from
    // focus genuinely leaving the widget (e.g. Tab), and so this doesn't double-call onBlur
    // alongside the outside-mousedown handler above. Attached to the wrapper (rather than just
    // the input) so tabbing away from the clear/toggle buttons is caught too.
    window.setTimeout(() => {
      if (!isOpenRef.current) return;
      if (wrapperRef.current?.contains(document.activeElement)) return;

      setIsOpen(false);
      setIsUserTyping(false);
      onBlur();
    }, 0);
  };

  const hasClearButton = !disabled && !!searchValue && isUserTyping;
  const hasSelectedOption = !disabled && !!value;

  const handleInputFocus = () => {
    if (disabled) return;

    if (value && !isUserTyping) {
      setSearchValue('');
      setIsUserTyping(true);
    }

    setIsOpen(true);
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      setHighlightedIndex((current) => (current + 1 >= filteredOptions.length ? 0 : current + 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      setHighlightedIndex((current) => (current - 1 < 0 ? filteredOptions.length - 1 : current - 1));
      return;
    }

    if (event.key === 'Enter') {
      if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        event.preventDefault();
        handleSelect(filteredOptions[highlightedIndex]);
      }
      return;
    }

    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      setIsOpen(false);
      setIsUserTyping(false);
      onBlur();
    }
  };

  return (
    <Wrapper ref={wrapperRef} onBlur={handleWidgetBlur}>
      <InputWrapper>
        <StyledInput
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          className={`form-control${hasError ? ' error' : ''}`}
          value={searchValue}
          $hasClearButton={hasClearButton}
          onFocus={handleInputFocus}
          onClick={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          onChange={(event) => {
            setSearchValue(event.target.value);
            setIsUserTyping(true);
            setIsOpen(true);
            onChange('');
          }}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={`${name}-options`}
          aria-activedescendant={
            highlightedIndex >= 0 && filteredOptions[highlightedIndex]
              ? `${name}-option-${highlightedIndex}`
              : undefined
          }
        />
        <Controls>
          {hasClearButton && (
            <ClearButton type="button" aria-label={`Clear ${itemLabel} search`} onClick={handleClear}>
              <FontAwesomeIcon icon={faCircleXmark} />
            </ClearButton>
          )}
          {!disabled && !hasSelectedOption && (
            <>
              {hasClearButton && <Divider aria-hidden="true" />}
              <ToggleButton
                type="button"
                aria-label={isOpen ? `Collapse ${itemLabel} options` : `Expand ${itemLabel} options`}
                aria-expanded={isOpen}
                onClick={() => setIsOpen((open) => !open)}
              >
                <Arrow open={isOpen} />
              </ToggleButton>
            </>
          )}
        </Controls>
      </InputWrapper>

      {isOpen && !disabled && (
        <OptionsList id={`${name}-options`} role="listbox" aria-label={`${name}-options`}>
          {filteredOptions.length ? (
            filteredOptions.map((option, index) => (
              <li key={option.value} role="presentation">
                <OptionButton
                  id={`${name}-option-${index}`}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  aria-selected={value === option.value}
                  selected={value === option.value}
                  highlighted={index === highlightedIndex}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </OptionButton>
              </li>
            ))
          ) : (
            <EmptyState>No matching {itemLabel}s</EmptyState>
          )}
        </OptionsList>
      )}
    </Wrapper>
  );
};

export default SearchableSelectField;
